import { Injectable } from '@angular/core';
import { FiscalMonthDTO } from '@libs/api';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

export interface FiscalCalendarData {
  months: FiscalMonthDTO[];
  quarters: Record<string, { startDate: string; endDate: string }>;
  years: Record<string, { startDate: string; endDate: string }>;
}

export interface FiscalWeek {
  weekNumber: number;
  year: number;
  startDate: dayjs.Dayjs;
  endDate: dayjs.Dayjs;
}

export interface PresetOption {
  label: string;
  value: string;
  order: number;
}

export interface PresetDateRange {
  startDate: dayjs.Dayjs;
  endDate: dayjs.Dayjs;
}

@Injectable({ providedIn: 'root' })
export class FiscalDatePresetService {
  private calendarData: FiscalCalendarData | null = null;
  private fiscalWeeks: FiscalWeek[] = [];

  setCalendarData(data: FiscalCalendarData): void {
    this.calendarData = data;
    this.fiscalWeeks = this.deriveWeeks(data.months);
  }

  readonly datePresetOptions: readonly PresetOption[] = [
    { label: 'Last 7 days', value: 'Latest 7 Days', order: 1 },
    { label: 'Last 14 days', value: 'Latest 14 Days', order: 2 },
    { label: 'Last 30 days', value: 'Latest 30 Days', order: 3 },
    { label: 'Last 90 days', value: 'Latest 90 Days', order: 4 },
    { label: 'Last 52 fiscal weeks', value: 'last_52_fiscal_weeks', order: 5 },
    { label: 'This fiscal week (WTD)', value: 'this_fiscal_week', order: 6 },
    { label: 'This fiscal month (MTD)', value: 'this_fiscal_month', order: 7 },
    {
      label: 'This fiscal quarter (QTD)',
      value: 'this_fiscal_quarter',
      order: 8
    },
    { label: 'This fiscal year (YTD)', value: 'this_fiscal_year', order: 9 },
    { label: 'Last fiscal week', value: 'last_fiscal_week', order: 10 },
    { label: 'Last fiscal month', value: 'last_fiscal_month', order: 11 },
    { label: 'Last fiscal quarter', value: 'last_fiscal_quarter', order: 12 },
    { label: 'Last fiscal year', value: 'last_fiscal_year', order: 13 },
    { label: 'Custom', value: 'custom', order: 14 }
  ];

  readonly comparePresetOptions: readonly PresetOption[] = [
    { label: 'Previous period', value: 'Previous Period', order: 1 },
    {
      label: 'Previous period (match day of week)',
      value: 'Previous Period (match day of the week)',
      order: 2
    },
    { label: 'Previous fiscal year', value: 'previous_fiscal_year', order: 3 },
    {
      label: 'Previous fiscal year (match day of week)',
      value: 'previous_fiscal_year_match_dow',
      order: 4
    },
    { label: 'Custom', value: 'custom', order: 5 }
  ];

  calculateDateRange(
    presetValue: string,
    maxDate?: dayjs.Dayjs
  ): PresetDateRange | null {
    const today = dayjs().utc(true);

    switch (presetValue) {
      case 'Latest 7 Days':
        return this.lastNDays(today, 7);
      case 'Latest 14 Days':
        return this.lastNDays(today, 14);
      case 'Latest 30 Days':
        return this.lastNDays(today, 30);
      case 'Latest 90 Days':
        return this.lastNDays(today, 90);
      case 'last_fiscal_week':
        return this.lastFiscalWeek(today);
      case 'last_52_fiscal_weeks':
        return this.last52FiscalWeeks(today);
      case 'last_fiscal_month':
        return this.lastFiscalMonth(today);
      case 'last_fiscal_quarter':
        return this.lastFiscalQuarter(today);
      case 'last_fiscal_year':
        return this.lastFiscalYear(today);
      case 'this_fiscal_week':
        return this.capEndDate(this.thisFiscalWeek(today), maxDate);
      case 'this_fiscal_month':
        return this.capEndDate(this.thisFiscalMonth(today), maxDate);
      case 'this_fiscal_quarter':
        return this.capEndDate(this.thisFiscalQuarter(today), maxDate);
      case 'this_fiscal_year':
        return this.capEndDate(this.thisFiscalYear(today), maxDate);
      case 'custom':
      default:
        return null;
    }
  }

  calculateCompareDateRange(
    presetValue: string,
    currentRange: PresetDateRange,
    currentDatePreset?: string
  ): PresetDateRange | null {
    switch (presetValue) {
      case 'Previous Period':
        return this.previousPeriod(currentRange);
      case 'Previous Period (match day of the week)':
        return this.previousPeriodMatchDow(currentRange);
      case 'previous_fiscal_year':
        return this.previousFiscalYear(currentRange, currentDatePreset);
      case 'previous_fiscal_year_match_dow':
        return this.previousFiscalYearMatchDow(currentRange, currentDatePreset);
      case 'custom':
      default:
        return null;
    }
  }

  private lastNDays(today: dayjs.Dayjs, n: number): PresetDateRange {
    return {
      startDate: today.subtract(n, 'day'),
      endDate: today.subtract(1, 'day')
    };
  }

  private lastFiscalWeek(today: dayjs.Dayjs): PresetDateRange | null {
    const currentWeek = this.findFiscalWeekForDate(today);
    if (!currentWeek) return null;

    const previousWeek = this.findPreviousFiscalWeek(currentWeek);
    if (!previousWeek) return null;

    return { startDate: previousWeek.startDate, endDate: previousWeek.endDate };
  }

  private last52FiscalWeeks(today: dayjs.Dayjs): PresetDateRange | null {
    const currentWeek = this.findFiscalWeekForDate(today);
    if (!currentWeek) return null;

    const lastCompletedWeek = this.findPreviousFiscalWeek(currentWeek);
    if (!lastCompletedWeek) return null;

    const endWeekIndex = this.fiscalWeeks.indexOf(lastCompletedWeek);
    if (endWeekIndex < 0) return null;

    const startIndex = endWeekIndex - 51;
    if (startIndex < 0) {
      // Not enough weeks, use the earliest available
      const firstWeek = this.fiscalWeeks[0];
      if (!firstWeek) return null;
      return {
        startDate: firstWeek.startDate,
        endDate: lastCompletedWeek.endDate
      };
    }

    const targetWeek = this.fiscalWeeks[startIndex];
    if (!targetWeek) return null;

    return {
      startDate: targetWeek.startDate,
      endDate: lastCompletedWeek.endDate
    };
  }

  private lastFiscalMonth(today: dayjs.Dayjs): PresetDateRange | null {
    const currentMonth = this.findCurrentFiscalMonth(today);
    if (!currentMonth) return null;

    const previousMonth = this.getPreviousFiscalMonth(currentMonth);
    if (!previousMonth) return null;

    return this.mapToPresetRange(previousMonth);
  }

  private lastFiscalQuarter(today: dayjs.Dayjs): PresetDateRange | null {
    const currentMonth = this.findCurrentFiscalMonth(today);
    if (!currentMonth) return null;

    let prevQuarter = currentMonth.quarter - 1;
    let prevYear = currentMonth.year;
    if (prevQuarter < 1) {
      prevQuarter = 4;
      prevYear -= 1;
    }

    return this.lookupQuarter(prevQuarter, prevYear);
  }

  private lastFiscalYear(today: dayjs.Dayjs): PresetDateRange | null {
    const currentMonth = this.findCurrentFiscalMonth(today);
    if (!currentMonth) return null;

    return this.lookupYear(currentMonth.year - 1);
  }

  private thisFiscalWeek(today: dayjs.Dayjs): PresetDateRange | null {
    const week = this.findFiscalWeekForDate(today);
    if (!week) return null;

    return { startDate: week.startDate, endDate: week.endDate };
  }

  private thisFiscalMonth(today: dayjs.Dayjs): PresetDateRange | null {
    const month = this.findCurrentFiscalMonth(today);
    if (!month) return null;

    return this.mapToPresetRange(month);
  }

  private thisFiscalQuarter(today: dayjs.Dayjs): PresetDateRange | null {
    const currentMonth = this.findCurrentFiscalMonth(today);
    if (!currentMonth) return null;

    return this.lookupQuarter(currentMonth.quarter, currentMonth.year);
  }

  private thisFiscalYear(today: dayjs.Dayjs): PresetDateRange | null {
    const currentMonth = this.findCurrentFiscalMonth(today);
    if (!currentMonth) return null;

    return this.lookupYear(currentMonth.year);
  }

  private previousPeriod(currentRange: PresetDateRange): PresetDateRange {
    const duration = currentRange.endDate.diff(currentRange.startDate, 'day');
    const newEnd = currentRange.startDate.subtract(1, 'day');
    const newStart = newEnd.subtract(duration, 'day');

    return { startDate: newStart, endDate: newEnd };
  }

  private previousPeriodMatchDow(
    currentRange: PresetDateRange
  ): PresetDateRange {
    const previousDate = this.previousPeriod(currentRange);
    return this.adjustToMatchWeekday(currentRange, previousDate, true);
  }

  private previousFiscalYear(
    currentRange: PresetDateRange,
    currentDatePreset?: string
  ): PresetDateRange {
    const { startDate, endDate } = currentRange;

    return (
      this.resolvePriorFromDatePreset(startDate, currentDatePreset) ??
      this.resolvePriorFiscalWeekRange(startDate, endDate) ?? {
        startDate: startDate.subtract(1, 'year'),
        endDate: endDate.subtract(1, 'year')
      }
    );
  }

  private resolvePriorFromDatePreset(
    startDate: dayjs.Dayjs,
    currentDatePreset?: string
  ): PresetDateRange | null {
    const currentMonth = this.findCurrentFiscalMonth(startDate);

    switch (currentDatePreset) {
      case 'last_fiscal_year': {
        const fiscalYear =
          currentMonth?.year ?? this.findFiscalWeekForDate(startDate)?.year;
        return fiscalYear ? this.lookupYear(fiscalYear - 1) : null;
      }
      case 'last_fiscal_quarter':
        return currentMonth
          ? this.lookupQuarter(currentMonth.quarter, currentMonth.year - 1)
          : null;
      case 'last_fiscal_month': {
        if (!currentMonth) return null;
        const previousMonth = this.findFiscalMonth(
          currentMonth.month,
          currentMonth.year - 1
        );
        return previousMonth ? this.mapToPresetRange(previousMonth) : null;
      }
      default:
        return null;
    }
  }

  private resolvePriorFiscalWeekRange(
    startDate: dayjs.Dayjs,
    endDate: dayjs.Dayjs
  ): PresetDateRange | null {
    const startWeek = this.findFiscalWeekForDate(startDate);
    const endWeek = this.findFiscalWeekForDate(endDate);
    if (
      !startWeek ||
      !endWeek ||
      !startDate.isSame(startWeek.startDate, 'day') ||
      !endDate.isSame(endWeek.endDate, 'day')
    ) {
      return null;
    }

    const prevStart = this.findFiscalWeekInYear(startWeek, startWeek.year - 1);
    const prevEnd = this.findFiscalWeekInYear(endWeek, endWeek.year - 1);
    if (!prevStart || !prevEnd) return null;

    return { startDate: prevStart.startDate, endDate: prevEnd.endDate };
  }

  private findFiscalWeekInYear(
    week: FiscalWeek,
    fiscalYear: number
  ): FiscalWeek | undefined {
    const weeksInYear = this.fiscalWeeks.filter((fw) => fw.year === fiscalYear);
    if (!weeksInYear.length) return undefined;

    // Clamp to the target year's last week when the requested week number doesn't exist (53 vs 52-week years).
    const maxWeekNumber = Math.max(...weeksInYear.map((fw) => fw.weekNumber));
    const targetWeekNumber = Math.min(week.weekNumber, maxWeekNumber);

    return weeksInYear.find((fw) => fw.weekNumber === targetWeekNumber);
  }

  private previousFiscalYearMatchDow(
    currentRange: PresetDateRange,
    currentDatePreset?: string
  ): PresetDateRange {
    const previousDate = this.previousFiscalYear(
      currentRange,
      currentDatePreset
    );
    return this.adjustToMatchWeekday(currentRange, previousDate, false);
  }

  private capEndDate(
    range: PresetDateRange | null,
    maxDate?: dayjs.Dayjs
  ): PresetDateRange | null {
    if (!range || !maxDate) return range;
    if (range.endDate.isAfter(maxDate, 'day')) {
      return { ...range, endDate: maxDate };
    }
    return range;
  }

  private toUtcDay(date: string): dayjs.Dayjs {
    return dayjs(date).utc(true);
  }

  private mapToPresetRange(range: {
    startDate: string;
    endDate: string;
  }): PresetDateRange {
    return {
      startDate: this.toUtcDay(range.startDate),
      endDate: this.toUtcDay(range.endDate)
    };
  }

  private lookupQuarter(quarter: number, year: number): PresetDateRange | null {
    const data = this.calendarData?.quarters[`${quarter}-${year}`];
    return data ? this.mapToPresetRange(data) : null;
  }

  private lookupYear(year: number): PresetDateRange | null {
    const data = this.calendarData?.years[`${year}`];
    return data ? this.mapToPresetRange(data) : null;
  }

  private isDateInRange(
    date: dayjs.Dayjs,
    start: dayjs.Dayjs,
    end: dayjs.Dayjs
  ): boolean {
    return !date.isBefore(start, 'day') && !date.isAfter(end, 'day');
  }

  private deriveWeeks(months: FiscalMonthDTO[]): FiscalWeek[] {
    const weeks: FiscalWeek[] = [];

    for (const month of months) {
      const monthStart = this.toUtcDay(month.startDate);
      const monthEnd = this.toUtcDay(month.endDate);
      let weekStart = monthStart;
      let weekNum = month.weekNumber;

      while (
        weekStart.isBefore(monthEnd, 'day') ||
        weekStart.isSame(monthEnd, 'day')
      ) {
        let weekEnd = weekStart.add(6, 'day');

        // Clamp the end of the last week to the month boundary
        if (weekEnd.isAfter(monthEnd, 'day')) {
          weekEnd = monthEnd;
        }

        weeks.push({
          weekNumber: weekNum,
          year: month.year,
          startDate: weekStart,
          endDate: weekEnd
        });

        weekNum++;
        weekStart = weekEnd.add(1, 'day');
      }
    }

    return weeks;
  }

  private findCurrentFiscalMonth(
    date: dayjs.Dayjs
  ): FiscalMonthDTO | undefined {
    if (!this.calendarData) return undefined;

    return this.calendarData.months.find((m) =>
      this.isDateInRange(
        date,
        this.toUtcDay(m.startDate),
        this.toUtcDay(m.endDate)
      )
    );
  }

  private findFiscalWeekForDate(date: dayjs.Dayjs): FiscalWeek | undefined {
    return this.fiscalWeeks.find((w) =>
      this.isDateInRange(date, w.startDate, w.endDate)
    );
  }

  private findPreviousFiscalWeek(
    currentWeek: FiscalWeek
  ): FiscalWeek | undefined {
    const index = this.fiscalWeeks.indexOf(currentWeek);
    return index > 0 ? this.fiscalWeeks[index - 1] : undefined;
  }

  private getPreviousFiscalMonth(
    currentMonth: FiscalMonthDTO
  ): FiscalMonthDTO | undefined {
    let prevMonthNum = currentMonth.month - 1;
    let prevYear = currentMonth.year;
    if (prevMonthNum < 1) {
      prevMonthNum = 12;
      prevYear -= 1;
    }

    return this.findFiscalMonth(prevMonthNum, prevYear);
  }

  private findFiscalMonth(
    month: number,
    year: number
  ): FiscalMonthDTO | undefined {
    return this.calendarData?.months.find(
      (m) => m.month === month && m.year === year
    );
  }

  private getNearestDayAdjustment(
    targetWeekday: number,
    currentWeekday: number
  ): number {
    const forwardDiff = (targetWeekday - currentWeekday + 7) % 7;
    const backwardDiff = (currentWeekday - targetWeekday + 7) % 7;

    return backwardDiff < forwardDiff ? -backwardDiff : forwardDiff;
  }

  private adjustToMatchWeekday(
    currentRange: PresetDateRange,
    previousRange: PresetDateRange,
    backwardOnly: boolean
  ): PresetDateRange {
    const currentStartDow = currentRange.startDate.day();
    const previousStartDow = previousRange.startDate.day();

    if (currentStartDow === previousStartDow) {
      return previousRange;
    }

    if (backwardOnly) {
      const dayAdjustment = (previousStartDow - currentStartDow + 7) % 7;
      return {
        startDate: previousRange.startDate.subtract(dayAdjustment, 'day'),
        endDate: previousRange.endDate.subtract(dayAdjustment, 'day')
      };
    }

    const dayAdjustment = this.getNearestDayAdjustment(
      currentStartDow,
      previousStartDow
    );

    return {
      startDate: previousRange.startDate.add(dayAdjustment, 'day'),
      endDate: previousRange.endDate.add(dayAdjustment, 'day')
    };
  }
}
